import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { GAService } from '@domgen/dgx-fe-ga';
import { ReplaySubject } from 'rxjs';
import { MMRContactHours } from '../types/manage-my-repair-types';
import { ErrorRetryService, RetryOnErrorConfig } from './error-retry.service';
import { CONFIG } from '@domgen/dgx-fe-config';
import { AppConfig } from '../types/app-config.model';
import { AccountApiType } from '../types/accountApiType';
import { ENVIRONMENT } from '@domgen/dgx-components';

export type ServiceCacheObject = {
  data: ReplaySubject<any>;
  staticData: any;
  loading: ReplaySubject<boolean>;
  updating: ReplaySubject<boolean>;
  error: ReplaySubject<HttpErrorResponse>;
  lastUpdated: ReplaySubject<Date>;
  dataLoaded: boolean;
};

enum AuthHeaderNames {
  REQUEST_SOURCE = 'request-source',
  REQUEST_ACTION = 'request-action',
  CLIENT = 'wl-client',
}

@Injectable({
  providedIn: 'root',
})
export class EngineerTrackingService {
  headers = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    [AuthHeaderNames.REQUEST_SOURCE]:
      this.environment.requestSource || 'DandGUK',
    [AuthHeaderNames.REQUEST_ACTION]:
      this.environment.requestAction || 'MyAccount',
    [AuthHeaderNames.CLIENT]: this.environment.requestSource || 'DandGUK',
  };

  httpOptions = {
    headers: new HttpHeaders({
      ...this.headers,
    }),
    withCredentials: true,
  };

  loadedEngineerTracking: Record<string, ServiceCacheObject> = {};

  constructor(
    private httpClient: HttpClient,
    private errorRetryService: ErrorRetryService,
    @Inject(ENVIRONMENT) private environment: any,
    @Inject(CONFIG) private readonly config: AppConfig,
    private GaService: GAService
  ) {}

  getEngineerTrackingData(
    claimId: string,
    planNumber: string
  ): ServiceCacheObject {
    if (!claimId) throw Error('No claim id');
    if (!this.loadedEngineerTracking[claimId]) {
      this.loadedEngineerTracking[claimId] = {
        data: new ReplaySubject(1),
        loading: new ReplaySubject(1),
        updating: new ReplaySubject(1),
        lastUpdated: new ReplaySubject(1),
        error: new ReplaySubject(1),
        dataLoaded: false,
        staticData: undefined,
      };
    } else {
      // Reset any errors that have happened
      this.loadedEngineerTracking[claimId].error.next(null);
    }

    this.loadEngineerTracking(claimId, planNumber);

    return this.loadedEngineerTracking[claimId];
  }

  loadEngineerTracking(claimId: string, planNumber: string): void {
    if (this.loadedEngineerTracking[claimId].dataLoaded) {
      this.loadedEngineerTracking[claimId].updating.next(true);
    } else {
      this.loadedEngineerTracking[claimId].loading.next(true);
    }

    this.getEngineerTracking(claimId, planNumber);
  }

  private getEngineerTracking(claimId: string, planNumber: string): void {
    const retryConfig: Partial<RetryOnErrorConfig> = {
      maxRetryCount: 2,
      conditionToRetryRequest: (error) => {
        const errorsToRetry = ['AT010'];

        return errorsToRetry.includes(error?.error?.ErrorCode);
      },
      logicToRunOnAttempt: () => {
        this.loadedEngineerTracking[claimId].error.next(null);
      },
      logicToRunOnError: (error: HttpErrorResponse) => {
        this.GaService.tag({
          event: 'genericGAEvent',
          clientName: 'DG My Account',
          pagename: 'claims-track-your-repair',
          eventCategory: 'track-your-repair',
          eventAction: 'something-went-wrong',
          eventLabel: 'error code',
          errorTrackRepair: error?.error?.ErrorCode,
        });
      },
    };

    const engineerTrackingRequest = {
      claimID: claimId,
      planNumber,
      channelCode: 'DGX',
      countryCode: 'GB',
    };

    const dateOfRequestStart = new Date();

    if (this.loadedEngineerTracking[claimId].staticData?.engineerEnRoute) {
      this.getEngineerTrackingForEnRoute(engineerTrackingRequest, retryConfig);
      return;
    }

    this.errorRetryService
      .retryOnError(
        this.httpClient.post(
          `${this.config.api}${AccountApiType.GetEngineerTracking}`,
          engineerTrackingRequest,
          this.httpOptions
        ),
        retryConfig
      )
      .subscribe({
        next: (data: { result: EngineerTrackingResponse; status: string }) => {
          this.handleSuccessfulResponse(
            claimId,
            data.result,
            dateOfRequestStart
          );
        },
        error: (error) => {
          if (error.error.ErrorCode === 'AT012') {
            this.getEngineerTrackingForEnRoute(
              engineerTrackingRequest,
              retryConfig
            );
            return;
          }
          this.handleErrorResponse(claimId, error);
        },
      });
  }

  private handleSuccessfulResponse(
    claimId: string,
    result: EngineerTrackingResponse,
    dateOfRequestStart: Date
  ): void {
    if (result.trackingData) {
      // TODO: Remove once Orbit send back properly formatted data...
      result.trackingData.engineerCurrentAppointment = parseInt(
        `${result.trackingData.engineerCurrentAppointment}`
      );
    }

    this.loadedEngineerTracking[claimId].staticData = result;
    this.loadedEngineerTracking[claimId].data.next(result);
    this.loadedEngineerTracking[claimId].dataLoaded = true;
    this.loadedEngineerTracking[claimId].lastUpdated.next(dateOfRequestStart);
    this.loadedEngineerTracking[claimId].loading.next(false);
    this.loadedEngineerTracking[claimId].updating.next(false);
  }

  private handleErrorResponse(claimId: string, error: HttpErrorResponse): void {
    // Comment out when testing using mock data
    this.loadedEngineerTracking[claimId].error.next(error);
    this.loadedEngineerTracking[claimId].loading.next(false);
    this.loadedEngineerTracking[claimId].updating.next(false);
  }

  private getEngineerTrackingForEnRoute(
    engineerTrackingRequest,
    retryConfig: Partial<RetryOnErrorConfig>
  ): void {
    const dateOfRequestStart = new Date();

    const claimId = engineerTrackingRequest.claimID;
    this.errorRetryService
      .retryOnError(
        this.httpClient.post(
          `${this.config.api}${AccountApiType.GetEngineerTracking}`,
          {
            ...engineerTrackingRequest,
            includeTracking: false,
          },
          this.httpOptions
        ),
        retryConfig
      )
      .subscribe({
        next: (data: { result: EngineerTrackingResponse; status: string }) => {
          this.handleSuccessfulResponse(
            claimId,
            data.result,
            dateOfRequestStart
          );
        },
        error: (error) => {
          this.handleErrorResponse(claimId, error);
        },
      });
  }
}

export interface EngineerTrackingResponse {
  status: string;
  statusCode: string;
  statusDebugging?: {
    source: string;
    description: string;
    resolution: string;
  };
  repairerName: string;
  repairerReference: string;
  repairerContactEmail: string;
  repairerContactTelephone: string;
  OEM: string;
  appointmentSlotType: string;
  appointmentSlotStart: string;
  appointmentSlotEnd: string;
  appointmentStatus: keyof typeof EngineerTrackingAppointmentStatus;
  trackingType: string;
  trackingData: EngineerTrackingData;
  claimAddress: ClaimAddress;
  contactHours: MMRContactHours;
  productDetails: EngineerTrackingProductDetails;
  engineerEnRoute?: boolean;
}

export interface EngineerTrackingProductDetails {
  manufacter: string;
  modelNumber: string;
  itemType: string;
  itemTypeGroup: string;
  serialNumber: string;
}

/**
 * @param {Object} 'REPAIR COMPLETED' = Repair completed
 * @param {Object} '1ST OUT CARD LEFT' = Visit failed - to be rearranged
 * @param {Object} 'REPAIR - CALL ON HOLD' = Repair in progress
 * @param {Object} '29 JOB CANCELLED' = Repair request cancelled (CANC or ABRT)
 * @param {Object} '2ND FIELD CALL REQUIRED' = Appointment required
 * @param {Object} 'PARTS REQUIRED' = Spare parts needed
 * @param {Object} '1ST FIELD CALL ARRANGED' = Repair in progress
 */
export const EngineerTrackingAppointmentStatus = {
  'REPAIR COMPLETED': 'REPAIR COMPLETED',
  '1ST OUT CARD LEFT': '1ST OUT CARD LEFT',
  'REPAIR - CALL ON HOLD': 'REPAIR - CALL ON HOLD',
  '29 JOB CANCELLED': '28 JOB CANCELLED',
  '2ND FIELD CALL REQUIRED': '2ND FIELD CALL REQUIRED',
  'PARTS REQUIRED': 'PARTS REQUIRED',
  '1ST FIELD CALL ARRANGED': '1ST FIELD CALL ARRANGED',
} as const;

export interface EngineerTrackingData {
  engineerName: string;
  engineerLongitude?: number;
  engineerLatitude?: number;
  engineerTotalAppointments: number;
  engineerCurrentAppointment: number;
  customerAppointment: number;
}
export interface ClaimAddress {
  addressLine1: string;
  addressLine2: string;
  addressLine3: string;
  addressLine4: string;
  postCode: string;
}

const EngineerTrackingResponseMock = (
  data: Partial<EngineerTrackingResponse> = {}
) => {
  const mock: EngineerTrackingResponse = {
    status: 'OK',
    statusCode: '200',
    repairerName: 'Test Repair',
    repairerReference: '123',
    repairerContactEmail: 'repairer@test.com',
    repairerContactTelephone: '08832345675',
    OEM: 'OEM',
    appointmentSlotType: '',
    appointmentSlotStart: '9:15',
    appointmentSlotEnd: '13:00',
    appointmentStatus: '1ST FIELD CALL ARRANGED',
    trackingType: 'SLOT',
    trackingData: {
      engineerName: 'Tester McTest',
      engineerTotalAppointments: 10,
      engineerCurrentAppointment: 5,
      customerAppointment: 8,
    },
    claimAddress: {
      addressLine1: '15 Hunting Avenue',
      addressLine2: 'Peterborough',
      addressLine3: '',
      addressLine4: '',
      postCode: 'PE2 9BJ',
    },
    contactHours: {
      monday: {
        openingTime: '08:00:00',
        closingTime: '18:30:00',
      },
      tuesday: {
        openingTime: '08:00:00',
        closingTime: '18:30:00',
      },
      wednesday: {
        openingTime: '08:00:00',
        closingTime: '18:30:00',
      },
      thursday: {
        openingTime: '08:00:00',
        closingTime: '18:30:00',
      },
      friday: {
        openingTime: '08:00:00',
        closingTime: '18:30:00',
      },
      saturday: {
        openingTime: '08:30:00',
        closingTime: '17:30:00',
      },
      sunday: {
        openingTime: '00:00:00',
        closingTime: '00:00:00',
      },
    },
    productDetails: {
      manufacter: 'test manufacturer',
      modelNumber: 'test model number',
      itemType: 'test item type',
      itemTypeGroup: 'test item group',
      serialNumber: 'test serial number',
    },
    ...data,
  };
  return mock;
};
